import * as React from 'react'
import Drawer from '@mui/joy/Drawer'
import IconButton from '@mui/joy/IconButton'
import Typography from '@mui/joy/Typography'
import Box from '@mui/joy/Box'
import CloseIcon from '@mui/icons-material/Close'
import { extractReplyContent } from '../utils/email'
import { copyToClipboard } from '../utils/util-functions'

interface EmailSidebarProps {
    open: boolean
    email: any | null
    thread: any[]
    threadCount: number
    threadFetchTime: number
    onClose: () => void
}

export default function EmailSidebar({ open, email, thread, threadCount, threadFetchTime, onClose }: EmailSidebarProps) {
    // Determine current user email address for alignment
    const userEmail = email?.emailData?.object?.from[0]?.email || ''

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            sx={{
                width: { xs: 420, sm: 600, md: 700 },
                maxWidth: '90vw',
                minWidth: '60vw',
                height: '100%',
                '.MuiDrawer-content': {
                    width: { xs: 420, sm: 600, md: 700 },
                    minWidth: '70vw',
                    maxWidth: '90vw',
                    height: '100%',
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: '1px solid #eee' }}>
                <Typography level="title-lg" flex={1}>
                    {email?.subject}
                </Typography>
                <IconButton onClick={onClose} size="sm">
                    <CloseIcon />
                </IconButton>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', px: 2, py: 1 }}>
                <Typography level="body-sm">
                    Thread messages: <b>{threadCount}</b>
                </Typography>
                <Typography level="body-sm" style={{ cursor: 'pointer' }} onClick={() => copyToClipboard(email?.emailData?.object?.thread_id)}>
                    Thread ID: <b>{email?.emailData?.object?.thread_id}</b>
                </Typography>
                <Typography level="body-sm">
                    Fetch time: <b>{threadFetchTime}</b> ms
                </Typography>
            </Box>
            <Box sx={{ p: 2, minHeight: 200, maxHeight: 'calc(100vh - 110px)', overflowY: 'auto' }}>
                {thread && thread.length > 0 ? (
                    thread.map((msg: any, idx: number) => {
                        const isSent = msg.emailData?.object?.from[0]?.email === userEmail
                        return (
                            <Box
                                key={msg._id || msg.id || idx}
                                sx={{
                                    mb: 2,
                                    display: 'flex',
                                    flexDirection: isSent ? 'row-reverse' : 'row',
                                    alignItems: 'flex-end',
                                }}
                            >
                                <Box
                                    sx={{
                                        bgcolor: isSent ? 'primary.softBg' : 'background.level2',
                                        color: isSent ? 'primary.solidColor' : 'text.primary',
                                        borderRadius: 10,
                                        px: 2,
                                        py: 1,
                                        minWidth: '80%',
                                        maxWidth: '90%',
                                        boxShadow: 1,
                                    }}
                                >
                                    <Typography level="body-sm" fontWeight={600} color={isSent ? 'primary' : 'neutral'}>
                                        {isSent ? 'You' : msg.emailData?.object?.from[0]?.name || msg.emailData?.object?.from[0]?.email || msg.sender}
                                    </Typography>
                                    <Typography level="body-xs" color="neutral" sx={{ mb: 0.5 }}>
                                        {msg.emailData?.object?.date ? new Date(msg.emailData.object.date).toLocaleString() : msg.date}
                                    </Typography>
                                    <Box
                                        sx={{
                                            mt: 1,
                                            wordBreak: 'break-word',
                                            fontSize: 15,
                                            lineHeight: 1.6,
                                            color: 'text.primary',
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html: extractReplyContent(msg.emailData?.object?.body || msg.body || ''),
                                        }}
                                    />
                                </Box>
                            </Box>
                        )
                    })
                ) : (
                    <Typography level="body-sm" color="neutral">
                        No thread messages found.
                    </Typography>
                )}
            </Box>
        </Drawer>
    )
}
